<script setup lang="ts">
import type { Team } from '~/types'
import { SocialNetwork } from '~/enums/social-network'

interface Props {
  team: Team.Model
}

const props = defineProps<Props>()
const {
  facebookLink,
  hasSlotContentBlocks,
  instagramLink,
  logo,
  menu,
  name,
  slotContentBlocks,
  slug,
  styles,
  tiktokLink,
  xLink,
  youtubeLink,
} = useTeam(props.team)
const isTeamMenuOpened = useState<boolean>('is-team-menu-opened', () => false)
</script>

<template>
  <header
    v-if="team"
    :class="{
      'team-header--mobile-menu-opened': isTeamMenuOpened,
    }"
    :style="styles"
    class="team-header"
  >
    <Wrapper class="team-header__wrapper">
      <NuxtLink
        class="team-header__logo"
        :to="{ name: 'teams-slug', params: { slug } }"
      >
        <span class="team-header__logo-image-wrapper">
          <UIImage
            v-if="logo"
            :image="logo"
            class="team-header__logo-image-wrapper-image"
          />
        </span>
        <span
          v-if="name"
          class="team-header__name"
        >
          {{ name }}
        </span>
      </NuxtLink>

      <div
        class="team-header__nav-wrapper"
        :class="{ 'team-header__nav-wrapper--active': isTeamMenuOpened }"
      >
        <nav
          :class="{ 'team-header__nav--opened': isTeamMenuOpened }"
          class="team-header__nav"
        >
          <div class="team-header__nav-wrapper">
            <HeaderMenu
              v-if="menu"
              :menu="menu"
              class="team-header__nav-list"
            />
          </div>
          <div class="team-header__socials">
            <SocialLink
              v-if="facebookLink"
              :href="facebookLink"
              :type="SocialNetwork.Facebook"
              variant="clear"
              class="team-header__socials-button"
            />
            <SocialLink
              v-if="xLink"
              :href="xLink"
              :type="SocialNetwork.X"
              variant="clear"
              class="team-header__socials-button"
            />
            <SocialLink
              v-if="instagramLink"
              :href="instagramLink"
              :type="SocialNetwork.Instagram"
              variant="clear"
              class="team-header__socials-button"
            />
            <SocialLink
              v-if="youtubeLink"
              :href="youtubeLink"
              :type="SocialNetwork.YouTube"
              variant="clear"
              class="team-header__socials-button"
            />
            <SocialLink
              v-if="tiktokLink"
              :href="tiktokLink"
              :type="SocialNetwork.TikTok"
              variant="clear"
              class="team-header__socials-button"
            />
          </div>
        </nav>
      </div>
      <ContentBlocks
        v-if="hasSlotContentBlocks"
        :content-blocks="slotContentBlocks"
        class="team-header__buttons"
      />
      <HeaderBurger
        :class="{ active: isTeamMenuOpened }"
        @click.prevent="(isTeamMenuOpened = !isTeamMenuOpened)"
      />
    </Wrapper>
  </header>
</template>

<style lang="scss">
  .team-header {
    @apply h-[70px] sticky left-0 top-0 w-full z-[10001] flex items-center bg-accent transition-colors duration-300;
    @apply xl:h-[100px];

    &--mobile-menu-opened {
      @apply max-xl:fixed max-xl:z-[10002];
    }

    &-overlay {
      @apply h-[70px] relative;
      @apply xl:h-[100px];

      &:has(.team-header--light) {
        @apply h-auto;
      }
    }

    &__wrapper {
      @apply w-full h-full flex items-center justify-between;
    }

    &__logo {
      @apply mr-[20px] flex items-center;
      @apply md:mr-[30px];
      @apply lg:mr-[60px];

      &-image-wrapper {
        @apply w-[40px] h-[40px] bg-primary/20 rounded-full mr-[10px] border border-white/20 inline-flex items-center justify-center shrink-0;
        @apply md:mr-[20px] md:w-[50px] md:h-[50px];

        &-image {
          @apply w-[70%] h-[70%] block object-contain;
        }
      }
    }

    &__name {
      @apply text-[var(--secondary-color)] text-[18px] font-medium leading-[1];
      @apply md:text-[20px];
      @apply lg:text-[28px];
    }

    &__nav {
      @apply flex flex-col h-full fixed top-[70px] pb-[100px] -right-full bg-accent w-full transition-all duration-300 overflow-hidden;
      @apply md:w-[400px] md:pb-0;
      @apply xl:static xl:w-full xl:bg-transparent xl:flex-row xl:items-center;

      &--opened {
        @apply right-0;
      }

      &-wrapper {
        @apply flex flex-col items-center;
        @apply xl:h-full xl:flex-row xl:mr-auto xl:grow;
      }

      &-list {
        @apply w-full;
      }

      &-list > .menu-list {
        @apply flex flex-col items-start px-[15px] gap-[20px] h-full py-[40px];
        @apply xl:flex-row xl:gap-x-[40px] xl:p-0 xl:overflow-visible xl:items-center xl:gap-[30px];
        @apply 2xl:gap-x-[40px];

        & > .menu-item > * {
          @apply text-[var(--secondary-color)] before:bg-[var(--secondary-color)];
        }

        .menu-item__top-level:is(.menu-item--has-submenu) > a::after,
        .menu-item__top-level:is(.menu-item--has-submenu) > span::after,
        .menu-item__top-level:is(.menu-item--has-submenu) > button::after {
          @apply border-t-[var(--secondary-color)] opacity-50;
        }

        .menu-item__submenu {
          @apply bg-[var(--team-color)];

          .menu-item__submenu-title,
          .menu-item__submenu-close,
          .menu-item__submenu-title .button__icon,
          .menu-item__submenu-close .button__icon,
          .column-menu-item__title,
          .menu-item--static-url > a,
          .menu-item--text > span,
          .menu-item--text > button {
            @apply text-[var(--secondary-color)];
          }

          .menu-item--static-url > a {
            @apply hover:decoration-[var(--secondary-color)];
          }
        }
      }
    }

    &__buttons {
      @apply ml-auto flex items-center gap-[10px];
      @apply xl:ml-[20px] xl:gap-[20px];
    }

    &__button {
      @apply text-[14px] w-[40px] h-[40px] rounded-full bg-white/15 text-[var(--secondary-color)] font-medium inline-flex items-center justify-center leading-[1] relative uppercase transition-colors duration-300;
      @apply xl:w-auto xl:h-auto xl:rounded-none xl:bg-transparent;
      @apply xl:before:content-[''] before:w-0 before:h-[2px] before:bg-[var(--secondary-color)] before:block before:transition-[width] before:duration-300 before:absolute before:-bottom-[4px] before:left-[22px];
      @apply xl:hover:before:w-[calc(100%-22px)];

      .iconify {
        @apply text-[var(--secondary-color)] w-[20px] h-[20px];
        @apply xl:text-[16px] xl:mr-[6px] xl:opacity-50;
      }

      span:not(.iconify) {
        @apply hidden;
        @apply xl:block;
      }
    }

    &__socials {
      @apply ml-[15px] flex items-center gap-[7px] mr-[10px];
      @apply md:gap-[5px];
      @apply xl:ml-auto;
    }

    &__socials-button {
      @apply text-[var(--secondary-color)] text-[22px] w-[40px] h-[40px] rounded-full bg-white/15 flex items-center justify-center transition-colors duration-300;
      @apply hover:text-white hover:bg-primary;

      .button__icon {
        @apply text-[var(--secondary-color)] w-[20px] h-[20px];
      }

      &:hover .button__icon {
        @apply text-white;
      }
    }
  }
</style>
